// @ts-strict-ignore
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { RedeemCode, RedeemCodeResponse } from 'mytours-read-api-typescript'
import {
	RootStoreState,
	redeemCode as redeemCodeAction,
	redeemBundleCoupon,
	getConfiguration,
	ERROR_MSG_REQUIRES_EMAIL_COUPON,
} from 'mytours-read-common'

import { ionHistory } from '../../navigation'
import {
	getTourUrl,
	formatLanguageUrl,
	BUNDLE_CODE_TYPE_INITIAL,
	PROJECT_CODE_TYPE_INITIAL,
} from '../../navigation/routes'
import { getBundleErrorMessage } from '../bundleErrorMessage'
import { parseItemId } from '../codeRedemption'
import { redeemedCodeOnDevice } from '../functions'
import Page from '../../common/components/Page'
import { Button, Card, Spinner, Input, Text } from '@mytours/components'
import strings from '../../../strings'

import '../styles.less'

interface MatchParams {
	code?: string
	itemId?: string
	itemType?: string
}

type Phase = 'confirming' | 'redeeming' | 'error'

type Props = RouteComponentProps<MatchParams> & { autoRedeem?: boolean }

// Map a non-valid redemption status to its user-facing message and whether retrying is worthwhile.
// Shared by the collection and app (whole-app) redemption result handlers so they stay in sync.
const mapRedeemErrorStatus = (status?: RedeemCodeResponse.StatusEnum): { message: string; retryable: boolean } => {
	switch (status) {
		case RedeemCodeResponse.StatusEnum.Invalid:
			return { message: strings.code_invalid, retryable: false }
		case RedeemCodeResponse.StatusEnum.Expired:
			return { message: strings.code_expired, retryable: false }
		case RedeemCodeResponse.StatusEnum.MaxRedemptions:
			return { message: strings.code_max_redemptions, retryable: false }
		case RedeemCodeResponse.StatusEnum.RequiresEmail:
			return { message: strings.code_requires_email, retryable: false }
		default:
			return { message: strings.code_redemption_error, retryable: true }
	}
}

const CodeRedeem: React.FC<Props> = ({ match, autoRedeem = false }) => {
	const dispatch = useDispatch()
	const lockAppCollection = useSelector((state: RootStoreState) => state.lockapp.collection)
	const lockAppApp = useSelector((state: RootStoreState) => state.lockapp.app)
	const lockAppLinkedId = useSelector((state: RootStoreState) => state.lockapp.linkedId)
	const lockAppLinkedType = useSelector((state: RootStoreState) => state.lockapp.linkedType)

	const code = useMemo(() => {
		const raw = match.params.code || ''
		try {
			return decodeURIComponent(raw)
		} catch {
			// Malformed percent-encoding (e.g. a hand-authored link with a bare '%') would otherwise
			// throw URIError during render and crash the whole page into the error boundary. Fall back
			// to the raw value and let the normal redeem/validation flow reject it. (codeRedemption.ts
			// guards the same decode for scanned URLs.)
			return raw
		}
	}, [match.params.code])
	const itemId = useMemo(() => parseItemId(match.params.itemId), [match.params.itemId])
	// No type initial → collection (back-compat with the existing collection QR); 'b' → bundle; 'p' → whole app (project).
	const isBundle = match.params.itemType === BUNDLE_CODE_TYPE_INITIAL
	const isProject = match.params.itemType === PROJECT_CODE_TYPE_INITIAL

	const bundleEntry = useSelector((state: RootStoreState) =>
		isBundle && itemId ? state.bundles.redeemedBundles[itemId] : undefined,
	)

	const [phase, setPhase] = useState<Phase>('confirming')
	const [errorMessage, setErrorMessage] = useState<string>('')
	const [isRetryable, setIsRetryable] = useState<boolean>(false)
	// A purchased code only redeems with its order's email (the server answers requires_email).
	// The emailed Redeem button and QR carry no email, so ask for it and redeem again.
	const [needsEmail, setNeedsEmail] = useState<boolean>(false)
	const [email, setEmail] = useState<string>('')
	// The server answers requires_email to a wrong email as well as to none, so a repeat after one
	// was submitted means it didn't match the order.
	const [emailSubmitted, setEmailSubmitted] = useState<boolean>(false)

	const linkedType = isBundle
		? RedeemCode.LinkedTypeEnum.Bundle
		: isProject
			? RedeemCode.LinkedTypeEnum.Project
			: RedeemCode.LinkedTypeEnum.Collection

	const dispatchRedeem = useCallback(
		(redeemEmail?: string) => {
			if (isBundle) {
				dispatch(
					redeemBundleCoupon.started({
						coupon: code,
						id: itemId,
						devicePlatform: getConfiguration().getPlatform(),
						email: redeemEmail,
					}),
				)
			} else {
				dispatch(
					redeemCodeAction.started({
						couponCode: code,
						linkedId: itemId,
						linkedType,
						devicePlatform: getConfiguration().getPlatform(),
						deviceUdid: getConfiguration().getDeviceUUID(),
						email: redeemEmail,
					}),
				)
			}
		},
		[code, itemId, isBundle, linkedType, dispatch],
	)

	const startRedemption = useCallback(async () => {
		if (!itemId) return
		setErrorMessage('')
		setIsRetryable(false)
		setPhase('redeeming')

		// Already redeemed on this device: skip the API call (re-redeeming needlessly consumes a
		// redemption and can fail with MaxRedemptions even though the device already has access) and go
		// straight to the unlocked content — the same destinations the success result handlers use.
		const stored = await redeemedCodeOnDevice(code, itemId, linkedType)
		if (stored) {
			ionHistory.replace(isBundle || isProject ? formatLanguageUrl() : getTourUrl(itemId, stored.accountId))
			return
		}

		dispatchRedeem()
	}, [code, itemId, isBundle, isProject, linkedType, dispatchRedeem])

	// Dispatch before leaving the error phase: the started action resets the slice synchronously, so
	// the result effects never see the previous requires_email outcome and bounce straight back.
	const redeemWithEmail = useCallback(() => {
		const trimmed = email.trim()
		if (!trimmed) return
		dispatchRedeem(trimmed)
		setEmailSubmitted(true)
		setErrorMessage('')
		setPhase('redeeming')
	}, [email, dispatchRedeem])

	// Collection redemption result (watches the lockapp slice) → navigate into the tour.
	useEffect(() => {
		if (isBundle || isProject || phase !== 'redeeming') return
		if (lockAppLinkedType !== RedeemCode.LinkedTypeEnum.Collection || lockAppLinkedId !== itemId) return
		if (lockAppCollection.loading || !lockAppCollection.loaded) return

		const { status, accountId } = lockAppCollection

		if (status === RedeemCodeResponse.StatusEnum.Valid) {
			ionHistory.replace(getTourUrl(itemId, accountId))
			return
		}

		const { message, retryable } = mapRedeemErrorStatus(status)
		setErrorMessage(message)
		setIsRetryable(retryable)
		setNeedsEmail(status === RedeemCodeResponse.StatusEnum.RequiresEmail)
		setPhase('error')
	}, [isBundle, isProject, phase, lockAppCollection, lockAppLinkedId, lockAppLinkedType, itemId])

	// Project (whole-app) redemption result (watches the lockapp slice). On success the whole app is
	// unlocked: lockapp.app becomes valid and the code is persisted to disk, so Home drops its lock
	// gate — returning to the root reveals the unlocked app.
	useEffect(() => {
		if (!isProject || phase !== 'redeeming') return
		if (lockAppLinkedType !== RedeemCode.LinkedTypeEnum.Project || lockAppLinkedId !== itemId) return
		if (lockAppApp.loading || !lockAppApp.loaded) return

		const { status } = lockAppApp

		if (status === RedeemCodeResponse.StatusEnum.Valid) {
			ionHistory.replace(formatLanguageUrl())
			return
		}

		const { message, retryable } = mapRedeemErrorStatus(status)
		setErrorMessage(message)
		setIsRetryable(retryable)
		setNeedsEmail(status === RedeemCodeResponse.StatusEnum.RequiresEmail)
		setPhase('error')
	}, [isProject, phase, lockAppApp, lockAppLinkedId, lockAppLinkedType, itemId])

	// Bundle redemption result (watches the bundles slice). On success the bundle unlocks globally
	// (redeemedBundles[id] + persisted disk code), so return home — its cards then show on its screen.
	useEffect(() => {
		if (!isBundle || phase !== 'redeeming' || !bundleEntry) return
		if (bundleEntry.redeemingCoupon) return

		// The attempt's error takes precedence: a failed (re-)redeem preserves an existing unlock,
		// so hasUnlockedBundle can be true even though the scanned code was just rejected.
		if (bundleEntry.redeemCouponError) {
			setErrorMessage(getBundleErrorMessage(bundleEntry.redeemCouponError) || strings.code_redemption_error)
			setIsRetryable(false)
			setNeedsEmail(bundleEntry.redeemCouponError.message === ERROR_MSG_REQUIRES_EMAIL_COUPON)
			setPhase('error')
			return
		}
		if (bundleEntry.hasUnlockedBundle) {
			ionHistory.replace(formatLanguageUrl())
		}
	}, [isBundle, phase, bundleEntry])

	// Auto-redeem links skip the confirmation card: fire the redemption on mount instead of waiting
	// for a tap. Keyed on phase === 'confirming' so it only kicks once — after a failure the user
	// stays on the error card and retries manually (it must not silently re-redeem).
	useEffect(() => {
		if (!autoRedeem || phase !== 'confirming' || !code || !itemId) return
		startRedemption()
	}, [autoRedeem, phase, code, itemId, startRedemption])

	const goHome = useCallback(() => {
		ionHistory.replace(formatLanguageUrl())
	}, [])

	// After a failed redeem, OK sends the user to the content target: the collection's page
	// (the lock screen if still locked) for a collection code, or home for app/bundle codes.
	const goToContentAfterFailure = useCallback(() => {
		ionHistory.replace(isBundle || isProject ? formatLanguageUrl() : getTourUrl(itemId))
	}, [isBundle, isProject, itemId])

	const renderConfirming = () => (
		<>
			<h1>{strings.code_redemption_confirm_title}</h1>
			<p>{strings.code_redemption_confirm_message}</p>
			<p className="code-display">
				<strong>{code}</strong>
			</p>
			<Button
				variant="primary"
				className="lock-button"
				aria-label={strings.code_redemption_confirm_button}
				onClick={startRedemption}
			>
				<Text variant="inherit">{strings.code_redemption_confirm_button}</Text>
			</Button>
			<Button variant="clear" aria-label={strings.cancel} onClick={goHome}>
				<Text variant="inherit">{strings.cancel}</Text>
			</Button>
		</>
	)

	const renderRedeeming = () => (
		<>
			<h1>{strings.code_redemption_confirm_title}</h1>
			<Spinner variant="crescent" aria-label={strings.loading} />
			{/* Auto-redeem enters this state on mount with no user action and the heading doesn't
			    change, so announce the in-progress redemption to screen readers. Assertive live region
			    (matching Loader's loading announcement) — more reliably announced than a polite
			    role="status" when the region and its text are inserted together. */}
			<p className="hidden" aria-live="assertive">
				{strings.loading}
			</p>
		</>
	)

	const renderEmailPrompt = () => (
		<>
			<h1>{strings.code_redemption_confirm_title}</h1>
			<p className="code-display">
				<strong>{code}</strong>
			</p>
			{/* role="alert" for the same reason as the error card: this replaces the spinner in place. Only one
			    of the prompt and the mismatch error carries it, so a rejected email isn't announced twice. */}
			<p role={emailSubmitted ? undefined : 'alert'}>{strings.code_enter_purchase_email}</p>
			<Input
				variant="ios"
				value={email}
				type="email"
				autoComplete="email"
				onChange={e => setEmail(e.target.value)}
				onKeyUp={e => e.key === 'Enter' && redeemWithEmail()}
				placeholder={strings.email}
				aria-label={strings.email}
			/>
			{emailSubmitted && (
				<div className="error" role="alert">
					{strings.code_email_mismatch}
				</div>
			)}
			<Button
				variant="primary"
				className="lock-button"
				aria-label={strings.code_redemption_confirm_button}
				onClick={redeemWithEmail}
			>
				<Text variant="inherit">{strings.code_redemption_confirm_button}</Text>
			</Button>
			<Button variant="clear" aria-label={strings.cancel} onClick={goHome}>
				<Text variant="inherit">{strings.cancel}</Text>
			</Button>
		</>
	)

	const renderError = () => (
		<>
			<h1>{strings.code_redemption_failed}</h1>
			{/* role="alert" so a failure reached via the in-place phase swap (no navigation/focus move,
			    e.g. an auto-redeem failure) is announced, matching Keypad's redemption error row. */}
			<div className="error" role="alert">
				{errorMessage}
			</div>
			{isRetryable ? (
				<>
					<Button
						variant="primary"
						className="lock-button"
						aria-label={strings.code_redemption_confirm_button}
						onClick={startRedemption}
					>
						<Text variant="inherit">{strings.code_redemption_confirm_button}</Text>
					</Button>
					<Button variant="clear" aria-label={strings.cancel} onClick={goHome}>
						<Text variant="inherit">{strings.cancel}</Text>
					</Button>
				</>
			) : (
				<Button variant="primary" className="lock-button" aria-label={strings.ok} onClick={goToContentAfterFailure}>
					<Text variant="inherit">{strings.ok}</Text>
				</Button>
			)}
		</>
	)

	if (!code) {
		return (
			<Page contentProps={{ className: 'lock-app code-redeem' }} dataCy="page-code-redeem">
				<Card variant="ios" className="center-block">
					<h1>{strings.code_redemption_failed}</h1>
					<div className="error">{strings.code_invalid}</div>
					<Button variant="clear" aria-label={strings.cancel} onClick={goHome}>
						<Text variant="inherit">{strings.cancel}</Text>
					</Button>
				</Card>
			</Page>
		)
	}

	if (!itemId) {
		return (
			<Page contentProps={{ className: 'lock-app code-redeem' }} dataCy="page-code-redeem">
				<Card variant="ios" className="center-block">
					<h1>{strings.code_redemption_failed}</h1>
					<div className="error">{strings.code_redemption_error}</div>
					<Button variant="clear" aria-label={strings.cancel} onClick={goHome}>
						<Text variant="inherit">{strings.cancel}</Text>
					</Button>
				</Card>
			</Page>
		)
	}

	return (
		<Page contentProps={{ className: 'lock-app code-redeem' }} dataCy="page-code-redeem">
			<Card variant="ios" className="center-block">
				<i className="lock-icon fal lock-alt" />
				{phase === 'confirming' && renderConfirming()}
				{phase === 'redeeming' && renderRedeeming()}
				{phase === 'error' && (needsEmail ? renderEmailPrompt() : renderError())}
			</Card>
		</Page>
	)
}

export default CodeRedeem
